/**
 * Billing funnel tracking.
 *
 * Records three coarse, non-identifying steps so the admin dashboard can show
 * how many people reach the paywall versus how many actually pay:
 *   billing_view      — a pricing/upgrade/billing surface was shown
 *   checkout_start    — the user tapped Subscribe (web, Apple or Google Play)
 *   purchase_complete — a subscription was verified as active
 *
 * Writes go through the SECURITY DEFINER `record_billing_funnel` RPC; the
 * underlying table denies all direct client writes and is readable only by
 * super admins. No amounts, emails or tokens are recorded here.
 */
import { supabase } from "@/integrations/supabase/client";

export type BillingFunnelEvent = "billing_view" | "checkout_start" | "purchase_complete";

function currentPlatform(): string {
  if (typeof window === "undefined") return "ssr";
  const cap = (window as unknown as { Capacitor?: { getPlatform?: () => string } }).Capacitor;
  const native = cap?.getPlatform?.();
  if (native && native !== "web") return native;
  return "web";
}

/** Fire-and-forget: never blocks or breaks a billing flow. */
export function trackBillingFunnel(event: BillingFunnelEvent, platform?: string): void {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    void (supabase as any)
      .rpc("record_billing_funnel", { _event: event, _platform: platform ?? currentPlatform() })
      .then(() => undefined, () => undefined);
  } catch {
    // Analytics must never surface to the user.
  }
}
